import { Button } from "@chakra-ui/button";
import { Box } from "@chakra-ui/layout";
import { useCurrentTenant } from "features/auth/tenant.store";
import { showSuccess } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import SelectBaseAntd, {
  SelectBaseAntdProps,
} from "components/Input/SelectBaseAntd";
import ModalCadastroUnidade from "components/Features/Unidades/components/ModalCadastroUnidade";
import useUnidadesSimplificado from "core/features/unidades/useUnidadesSimplificado";
import { UnidadeSimplesPaginateResponse } from "features/unidades/typings/unidade";
import { normalizeString } from "infra/helpers/normalizeString";

export interface SelectUnidadeProps<T = any> extends SelectBaseAntdProps<T> {
  value: T | undefined;
  onChange: (v: any, option?: any) => void;
  tenantId?: string;
  blocoQuadraId?: string;
  gruposUnidadesIds?: string[];
  filteredOptions?: string[];
  includeGruposUnidadesLabel?: boolean;
}

function SelectUnidade({
  value,
  onChange,
  isInvalid,
  tenantId,
  blocoQuadraId,
  filteredOptions,
  gruposUnidadesIds,
  includeGruposUnidadesLabel = true,
  ...rest
}: SelectUnidadeProps) {
  const { tenant } = useCurrentTenant();
  // TODO, criar paginado com busca
  const [search, setSearch] = useState<string>();

  const { isLoading, isFetching, data, refetch } = useUnidadesSimplificado(
    tenantId || tenant.id,
    { blocoQuadraId, limit: 99999, gruposUnidadesIds }
  );

  const [isOpenModal, setIsOpenModal] = useState(false);

  useEffect(() => {
    if (!isOpenModal) {
      setSearch("");
    }
  }, [isOpenModal]);

  const handleSuccess = async () => {
    setIsOpenModal(false);

    await showSuccess(`Unidade salva com sucesso`);

    refetch();
  };

  const _isLoading = isLoading || isFetching;

  const getLabelBusca = (item: UnidadeSimplesPaginateResponse) => {
    let labelBusca = "";

    const nomeResponsavelLimpo = normalizeString(item.responsavel ?? "");

    const _search = search?.toLowerCase();

    if (_search?.startsWith("proprietario:")) {
      labelBusca += `${item.responsavel ?? ""} ${nomeResponsavelLimpo}`;

      return labelBusca;
    }

    if (_search?.startsWith("unidade:")) {
      labelBusca += `${
        !blocoQuadraId && item.blocoQuadra ? item.blocoQuadra.nome + "," : ""
      } ${item.numero}`;

      return labelBusca;
    }

    if (_search?.startsWith("grupo:")) {
      if (item.gruposUnidade?.length) {
        item.gruposUnidade.forEach((grupo) => {
          const nomeGrupoLimpo = normalizeString(grupo.nome ?? "");

          labelBusca += `${grupo.nome} ${nomeGrupoLimpo} `;
        });
      }

      return labelBusca;
    }

    // Incluimos a string normal e a string limpa para que a busca funcione
    // com e sem acentos.

    labelBusca += `${
      !blocoQuadraId && item.blocoQuadra ? item.blocoQuadra.nome + "," : ""
    } ${item.numero} ${item.responsavel ?? ""} ${nomeResponsavelLimpo}`;

    if (item.gruposUnidade?.length && includeGruposUnidadesLabel) {
      item.gruposUnidade.forEach((grupo) => {
        const nomeGrupoLimpo = normalizeString(grupo.nome ?? "");

        labelBusca += ` ${grupo.nome} ${nomeGrupoLimpo}`;
      });
    }

    return labelBusca;
  };

  return (
    <>
      {isOpenModal ? (
        <ModalCadastroUnidade
          tenantId={tenantId || tenant.id}
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          onSaved={handleSuccess}
        />
      ) : null}

      <SelectBaseAntd
        isInvalid={isInvalid}
        loading={_isLoading}
        size="large"
        placeholder="Selecione a unidade"
        style={{ width: "100%" }}
        value={value}
        onChange={onChange}
        allowClear={false}
        {...rest}
        showSearch
        searchValue={search}
        onSearch={(v) => {
          if (!isOpenModal) {
            setSearch(v);
          }
        }}
        filterOption={(inputValue, option) => {
          const realInputValue = inputValue
            ?.toLowerCase()
            .replace(/proprietario:|unidade:|grupo:/, "");

          return option?.labelBusca
            ?.toLowerCase()
            .includes(realInputValue?.toLowerCase());
        }}
        optionFilterProp="labelBusca"
        options={data?.items
          .filter((item) =>
            filteredOptions ? !filteredOptions.includes(item.id) : true
          )
          .map((item) => ({
            value: item.id,
            label: `${
              !blocoQuadraId && item.blocoQuadra
                ? item.blocoQuadra.nome + ", "
                : ""
            } ${item.numero}`,
            labelBusca: getLabelBusca(item),
          }))}
        notFoundContent={
          !_isLoading ? (
            <Box>
              Nenhuma unidade encontrada{" "}
              <Button
                isLoading={isLoading}
                leftIcon={<i className="mdi mdi-plus" />}
                size="xs"
                colorScheme="orange"
                onClick={() => setIsOpenModal(true)}
              >
                cadastrar nova
              </Button>
            </Box>
          ) : (
            <Box>Carregando...</Box>
          )
        }
      />
    </>
  );
}

export default SelectUnidade;
